import { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Background from "../components/Background";
import AyupathCard from "../components/AyupathCard";
import IntakeForm, { checkoutRequestId } from "../components/IntakeForm";
import SecondaryButton from "../components/SecondaryButton";
import { StarIcon } from "../components/icons";
import { track } from "../utils/analytics";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Stripe redirects here after payment. The order is recorded by the webhook,
// not by this page — she may never land here if she closes the tab.
const FULL_REPORT_SLA = process.env.REACT_APP_FULL_REPORT_SLA || "1 to 2 business days";

/**
 * Also serves /order-questions, the link in the order confirmation email,
 * for buyers who closed the tab before this page could ask them.
 */
export default function ThankYou() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const fromEmail = pathname === "/order-questions";
  const candidate = searchParams.get("requestId") || checkoutRequestId();
  const requestId = UUID.test(candidate) ? candidate : "";

  useEffect(() => {
    if (!fromEmail) track("purchase_confirmed");
  }, [fromEmail]);

  return (
    <Background>
      <div className="min-h-screen px-4 py-10 flex flex-col items-center">
        <div className="w-full max-w-md mx-auto flex-1 space-y-6">
          <AyupathCard>
            <div className="space-y-6">
              <div className="text-center space-y-3">
                <div className="mx-auto h-14 w-14 rounded-3xl bg-orange-100 grid place-items-center text-orange-600">
                  <StarIcon />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  {fromEmail ? "Tell us what matters most" : "Thank you — that's all sorted"}
                </h2>
                <p className="text-gray-600">
                  {fromEmail
                    ? "Your report is being prepared. Add your question and we'll answer it through your chart."
                    : "Your payment went through and your Next Chapter report is now being prepared."}
                </p>
              </div>

              {requestId ? <IntakeForm requestId={requestId} /> : null}

              <div className="rounded-2xl bg-orange-50 border border-orange-100 p-5 space-y-3">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-gray-900">It arrives within {FULL_REPORT_SLA}.</span>{" "}
                  It's written for your chart rather than generated on the spot, which is why
                  it takes a little while.
                </p>
                <p className="text-sm text-gray-700">
                  It'll come to the same email address as your preview. There's nothing
                  else you need to do.
                </p>
                <p className="text-sm text-gray-700">
                  If the report isn't useful to you, reply to it within 14 days of delivery
                  and we'll refund you in full.
                </p>
              </div>

              <p className="text-sm text-gray-600">
                If your birth details have changed, or anything looked wrong in your preview,
                just reply to your confirmation email and we'll correct it before we start.
              </p>

              <div className="text-center">
                <SecondaryButton onClick={() => navigate("/", { replace: true })}>
                  Back to Your Next Chapter
                </SecondaryButton>
              </div>
            </div>
          </AyupathCard>
        </div>
      </div>
    </Background>
  );
}
